import { CONSUMER_BANKING_TEAM_CODES, GIT_RELEASE_BRANCH } from '@/config'
import { isProduction } from '@/environment'
import { getRouter, paths } from '@/router'
import ApiService from '@/service'
import type {
  DeploymentStatus,
  Option,
  PolicyAnalysis,
  PolicyBranchInfo,
  PolicyTest,
  RequestTemplate,
  RuleFocus,
  StackStatus,
  UsedKeys,
  UsedValues,
} from '@/types'
import { LATEST_SUPPORTED_POLICY_FORMAT_VERSION } from '@/utils/constants'
import { getDeploymentMessage, getRegionNameFromUrl } from '@/utils/deploymentUtils'
import { cleanAwayInvalidKeysFromPolicyRules, migratePolicy } from '@/utils/migrations'
import notifications from '@/utils/notifications'
import * as testValueUtils from '@/utils/testValueUtils'
import { requiredPolicyTestFields } from '@/utils/testValueUtils'
import { alertUser, deepClone, draftBranchName } from '@/utils/utils'
import { AuthState } from '@/vuex/authModule'
import type {
  ApiSchema,
  CreatePolicyRequest,
  DeploymentRegion,
  Policy,
  PolicyDeploymentStatus,
  PolicyExecPayload,
  PolicyTestResult,
  PolicyWarnings,
  PullRequest,
  Rule,
  TestResult,
} from '@klarna/cbrms-policy-rules-manager-client-ts'
import axios from 'axios'
import { defineActions, defineGetters, defineModule, defineMutations } from 'direct-vuex'
import chunk from 'lodash/chunk'
import difference from 'lodash/difference'
import flatten from 'lodash/flatten'
import indexOf from 'lodash/indexOf'
import intersection from 'lodash/intersection'
import keys from 'lodash/keys'
import pick from 'lodash/pick'
import uniqBy from 'lodash/uniqBy'
import { moduleActionContext } from '.'

export const policyActionContext = (context: any) => moduleActionContext(context, policyModule)

let pollingTimer: number | undefined = undefined

const isTerminalStackStatus = (status: StackStatus) =>
  status.includes('COMPLETE') || status.includes('FAILED') || status.includes('NON_EXISTING_STACK')

const isTerminalDeploymentStatus = (status: DeploymentStatus) => status !== 'IN_PROGRESS' && status !== 'ROLL_BACK'

const isInTerminalState = (deployment: PolicyDeploymentStatus) =>
  isTerminalDeploymentStatus(deployment.deployment_status) && isTerminalStackStatus(deployment.status)

export interface PolicyRulesTestPayload {
  id: string
  content: string
}

const stringifyIfObject = (val: any) => (typeof val === 'object' ? JSON.stringify(val, null, 2) : val)

const isCreateNewBranch = (operation: string) => operation === 'CREATE_NEW_BRANCH'

/**
 * `fraud_us_plip` policy `id` used as a temporary means to identify
 * a policy containing large rules to be removed.
 */
const POLICY_FRAUD_US_PLIP_ID = 'fraud_us_plip'

/**
 * `fraud_reject_blocked_card_identifier` rule `id` used as a temporary means to identify
 * a rule that should be removed from a policy to prevent exceeding response byte size limits
 * from AWS Lambda.
 */
const RULE_FRAUD_REJECT_BLOCKED_CARD_IDENTIFIER_ID = 'fraud_reject_blocked_card_identifier'

/**
 * When refreshing outdated policy rules after creating a new draft,
 * `GET` requests will be sent in batches of `OUTDATED_RULES_BATCH_SIZE`.
 */
const OUTDATED_RULES_BATCH_SIZE = 5

export interface PolicyState {
  deploying: boolean
  isLoading: boolean
  // TODO: should initially be undefined
  currentPolicy: Policy
  currentBranch: string
  currentPr?: PullRequest
  pristineCurrentPolicy: Policy
  usedValues: UsedValues
  policyRunResult?: PolicyTestResult
  currentPolicyVersion?: string
  currentPolicyEngineVersion?: string
  policyDeploymentStatus?: PolicyDeploymentStatus
  policyDeploymentStatusLoadingState: 'idle' | 'success' | 'loading'
  ruleFocus: RuleFocus
  currentPolicyBranches: PolicyBranchInfo[]
  selectedLabels: Option[]
  selectedTeams: Option[]
  testResults?: (PolicyTestResult | TestResult | undefined)[]
  shownRuleResultIndex: number
  showDrafts: boolean
  isDraftOutdated?: boolean
  engineVersion: string
  isPRMerging: boolean
  isPRLoading: boolean
  policyAnalysis?: PolicyAnalysis
}

const policyModule = defineModule({
  namespaced: true,

  state: {
    // TODO: initial value should be undefined, but will required extensive rework of components
    currentPolicy: {} as Policy,
    currentBranch: GIT_RELEASE_BRANCH,
    currentPr: undefined,
    pristineCurrentPolicy: {} as Policy,
    usedValues: { inputs: {}, helpers: {} },
    policyRunResult: undefined,
    policyDeploymentStatus: undefined,
    currentPolicyVersion: undefined,
    currentPolicyEngineVersion: undefined,
    policyDeploymentStatusLoadingState: 'idle',
    ruleFocus: {
      baseOn: 'NONE',
      searchFor: undefined,
    },
    deploying: false,
    isLoading: false,
    policiesLoading: false,
    currentPolicyBranches: [],
    selectedLabels: [],
    selectedTeams: [],
    testResults: undefined,
    shownRuleResultIndex: -1,
    showDrafts: false,
    isDraftOutdated: false,
    engineVersion: '',
    isPRMerging: false,
    isPRLoading: false,
    policyAnalysis: { loading: false, result: '' },
  } as PolicyState,

  getters: defineGetters<PolicyState>()({
    isPolicyDeployed: ({ policyDeploymentStatus }: PolicyState): boolean => {
      if (policyDeploymentStatus && policyDeploymentStatus.deployment_status) {
        return ['FINISHED', 'NEW_DEPLOYMENT_BUILD_FAILED'].includes(policyDeploymentStatus.deployment_status)
      }

      return policyDeploymentStatus
        ? policyDeploymentStatus.status === 'CREATE_COMPLETE' || policyDeploymentStatus.status === 'UPDATE_COMPLETE'
        : false
    },

    rulesInCurrentPolicy(state: PolicyState): Rule[] {
      return state.currentPolicy.rules || []
    },

    currentPolicyUsedKeys(state: PolicyState, getters: any): UsedKeys {
      return {
        inputs: Array.from(
          new Set(
            flatten([
              // A policy always requires this regardless if it is used in a rule or not
              'cid',
              // A policy always requires this regardless if it is used in a rule or not
              'persona_id',
              ...getters.rulesInCurrentPolicy.map((r: Rule) => r.content_inputs || []),
            ])
          )
        ),
        helpers: Array.from(new Set(flatten(getters.rulesInCurrentPolicy.map((r: Rule) => r.content_helpers || [])))),
      }
    },

    deprecatedPolicyKeys(state: PolicyState, getters: any, rootState: any, rootGetters: any) {
      const deprecatedHelpers = intersection(
        getters.currentPolicyUsedKeys.helpers,
        rootGetters['scriptKey/deprecatedKeys'].deprecatedHelpers
      )
      const deprecatedInputs = intersection(
        getters.currentPolicyUsedKeys.inputs,
        rootGetters['scriptKey/deprecatedKeys'].deprecatedInputs
      )

      return [...deprecatedHelpers, ...deprecatedInputs]
    },

    currentPolicyHasOutdatedRules(state: PolicyState): boolean {
      const rules = state.currentPolicy ? state.currentPolicy.rules || [] : []
      return rules.reduce((acc: boolean, r: Rule) => {
        if (r.branch_name === 'release') {
          return acc || r.current_commit !== r.latest_release_commit
        } else {
          return acc || r.current_commit !== r.latest_commit
        }
      }, false)
    },

    displayedRules(state: PolicyState): Rule[] {
      const { showDrafts, selectedLabels, selectedTeams, currentPolicy } = state

      let rules = currentPolicy.rules || []

      if (showDrafts) {
        rules = (currentPolicy.rules || []).filter(rule => !rule.branch_name.includes(GIT_RELEASE_BRANCH))
      }

      if (selectedTeams.length > 0) {
        rules = rules.filter(rule => rule.team_name && state.selectedTeams.map(el => el.label).includes(rule.team_name))
      }

      if (selectedLabels.length > 0) {
        rules = rules.filter(
          rule =>
            difference(
              selectedLabels.map(el => el.value),
              rule.labels
            ).length === 0
        )
      }

      return rules
    },

    isRelease(state: PolicyState): boolean {
      return state.currentBranch === GIT_RELEASE_BRANCH
    },

    isMyBranch(state: PolicyState, getters: any, rootState: any) {
      const auth = rootState.auth as AuthState
      const username = auth.user && auth.user.username
      return username === state.currentBranch
    },
    myBranchExists(state: PolicyState, getters: any, rootState: any) {
      const auth = rootState.auth as AuthState
      const username = auth.user && auth.user.username
      return username && (getters.branchNames as string[]).includes(username)
    },
    areAllRulesRelease(state: PolicyState): boolean {
      const rulesInRelease = state.currentPolicy.rules.filter(rule => rule.branch_name === 'release')
      return rulesInRelease.length === state.currentPolicy.rules.length
    },
    branchNames(state: PolicyState) {
      return state.currentPolicyBranches.map(b => b.branch)
    },
    currentPolicyHasChanges(state: PolicyState) {
      const { currentPolicy, pristineCurrentPolicy } = state
      const mutableFields = [
        'id',
        'rules',
        'labels',
        'description',
        'unittests',
        'production_enabled',
        'jira_issue',
        'extensions',
      ]
      return (
        JSON.stringify(pick(currentPolicy, ...mutableFields)) !==
        JSON.stringify(pick(pristineCurrentPolicy, ...mutableFields))
      )
    },

    isOwningOrAdminTeam(state: PolicyState, getters: any, rootState: any, rootGetters: any) {
      const auth = rootState.auth as AuthState
      const policyTeamCode = state.currentPolicy.team_code
      const teamCode = auth.userInfo && auth.userInfo.team_code
      const additionalTeams = auth.userInfo && auth.userInfo.additional_teams

      // Iris team has admin rights
      if (rootGetters['auth/isAdminTeam']) {
        return true
      }

      if (!policyTeamCode) return false

      if (policyTeamCode === teamCode) {
        return true
        // Check if team code belongs to a team group
      } else if (additionalTeams && additionalTeams.length) {
        const teamGroup = additionalTeams.map(el => el.team_code)
        return teamGroup.includes(policyTeamCode)
      } else {
        // Migration of team code after the split of the Consumer Banking Support team
        if (CONSUMER_BANKING_TEAM_CODES.includes(policyTeamCode)) {
          return teamCode && CONSUMER_BANKING_TEAM_CODES.includes(teamCode)
        }
      }
    },

    isDeployedOutdated(state: PolicyState) {
      return (
        state.policyDeploymentStatus && state.policyDeploymentStatus.commit_id !== state.currentPolicy.current_commit
      )
    },

    policyHasNoDrafts(state: PolicyState) {
      return state.currentPolicyBranches && state.currentPolicyBranches.length === 1
    },
  }),

  mutations: defineMutations<PolicyState>()({
    // This is for unit test usage, so one can setup test state without being mega-intrusive
    REPLACE(state: PolicyState, payload: Partial<PolicyState>) {
      const keys = Object.keys(payload) as (keyof PolicyState)[]

      keys.forEach((key: keyof PolicyState) => {
        state[key] = payload[key] as never
      })
    },
    SET_POLICY_LOADING(state: PolicyState, isLoading: boolean) {
      state.isLoading = isLoading
    },

    SET_CURRENT_POLICY(state: PolicyState, { policy }: { policy: Policy }) {
      state.currentPolicy = policy
    },

    SET_PRISTINE_CURRENT_POLICY(state: PolicyState, payload: Policy) {
      state.pristineCurrentPolicy = payload
    },

    ADD_RULE_TO_CURRENT_POLICY(
      state: PolicyState,
      { rulesToAdd, isChallenger }: { rulesToAdd: Rule[]; isChallenger?: boolean }
    ) {
      const { currentPolicy } = state
      const { rules } = currentPolicy
      if (isChallenger) {
        const championIndex = currentPolicy.rules.findIndex(el => el.id === rulesToAdd[0].champion_id)
        currentPolicy.rules = uniqBy(rules.splice(championIndex + 1, 0, rulesToAdd[0]), 'id')
      }
      currentPolicy.rules = uniqBy([...rules, ...rulesToAdd], 'id')
    },

    SET_CONTENT_METADATA(
      state: PolicyState,
      {
        content_helpers,
        content_inputs,
      }: {
        content_helpers: string[]
        content_inputs: string[]
      }
    ) {
      const { currentPolicy } = state
      currentPolicy.content_helpers = content_helpers
      currentPolicy.content_inputs = content_inputs
    },

    UPDATE_POLICY_DETAILS(state: PolicyState, payload: Partial<Policy>) {
      state.currentPolicy = { ...state.currentPolicy, ...payload }
    },

    REMOVE_RULE_FROM_CURRENT_POLICY(state: PolicyState, ruleIds: string[]) {
      state.currentPolicy.rules = state.currentPolicy.rules.filter(rule => !ruleIds.includes(rule.id))
    },

    SET_POLICY_DEPLOYMENT_STATUS(state: PolicyState, payload: PolicyDeploymentStatus | undefined) {
      state.policyDeploymentStatus = payload
    },

    SET_POLICY_VERSION(state: PolicyState, version: string | undefined) {
      state.currentPolicyVersion = version
    },

    SET_POLICY_ENGINE_VERSION(state: PolicyState, version: string | undefined) {
      state.currentPolicyEngineVersion = version
    },

    SET_POLICY_DEPLOYMENT_STATUS_LOADING_STATE(
      state: PolicyState,
      payload: PolicyState['policyDeploymentStatusLoadingState']
    ) {
      state.policyDeploymentStatusLoadingState = payload
    },

    SET_DEPLOYING_FLAG(state: PolicyState, payload: boolean) {
      state.deploying = payload
    },

    SET_POLICY_RUN_VALUES(state: PolicyState, payload: UsedValues) {
      state.usedValues = payload
    },

    SET_POLICY_RUN_RESULT(state: PolicyState, payload: PolicyTestResult | undefined) {
      state.policyRunResult = payload
    },

    SET_RULE_FOCUS(state: PolicyState, { baseOn, searchFor }: RuleFocus) {
      state.ruleFocus = { baseOn, searchFor }
    },

    SET_POLICY_PULL_REQUEST(state: PolicyState, pr: PullRequest | undefined) {
      state.currentPr = pr
    },

    DELETED_POLICY_PULL_REQUEST(state: PolicyState) {
      state.currentPr = undefined
    },

    SET_CURRENT_POLICY_BRANCHES(state: PolicyState, branchInfos: PolicyBranchInfo[]) {
      // There's a bug in the Backend which can return undefined branchInfos
      state.currentPolicyBranches = branchInfos.filter(b => !!b)
    },
    SET_CURRENT_POLICY_BRANCH(state: PolicyState, branch: string) {
      state.currentBranch = branch
    },
    SET_SELECTED_LABELS(state: PolicyState, payload: Option[]) {
      state.selectedLabels = payload
    },
    SET_SELECTED_TEAMS(state: PolicyState, payload: Option[]) {
      state.selectedTeams = payload
    },
    SET_POLICY_TESTS(state: PolicyState, payload: PolicyTest[]) {
      state.currentPolicy.unittests = payload
    },
    REORDER_POLICY_TESTS(state: PolicyState, payload: { fromIndex: number; toIndex: number }) {
      if (!state.currentPolicy.unittests) return

      const { fromIndex, toIndex } = payload
      const test = state.currentPolicy.unittests[fromIndex]
      state.currentPolicy.unittests.splice(fromIndex, 1)
      state.currentPolicy.unittests.splice(toIndex, 0, test)
    },
    UPDATE_POLICY_TEST(state: PolicyState, { index, test }: { index: number; test: PolicyTest }) {
      const { unittests = [] } = state.currentPolicy
      const newUnitTests = [...unittests]
      newUnitTests[index] = test
      state.currentPolicy.unittests = newUnitTests
    },
    SET_POLICY_TEST_RESULTS(
      state: PolicyState,
      testResults: (PolicyTestResult | TestResult | undefined)[] | undefined
    ) {
      state.testResults = testResults
    },
    SET_CURRENT_POLICY_RULES_ORDER(state: PolicyState, payload: Rule[]) {
      state.currentPolicy.rules = payload
    },
    SET_POLICY_SHOWN_TEST_RESULT_INDEX(state: PolicyState, index: number) {
      state.shownRuleResultIndex = index
    },
    SHOW_DRAFTS(state: PolicyState, showDrafts: boolean) {
      state.showDrafts = showDrafts
    },
    REPLACE_RULE_IN_CURRENT_POLICY(state: PolicyState, { rule, index }: { rule: Rule; index: number }) {
      state.currentPolicy.rules.splice(index, 1, rule)
    },
    SET_DRAFT_IS_OUTDATED(state: PolicyState, status: boolean) {
      state.isDraftOutdated = status
    },
    SET_ENGINE_VERSION(state: PolicyState, version: string) {
      state.engineVersion = version
    },
    SET_IS_PR_MERGING(state: PolicyState, merging: boolean) {
      state.isPRMerging = merging
    },
    SET_IS_PR_LOADING(state: PolicyState, loading: boolean) {
      state.isPRLoading = loading
    },
    SET_POLICY_ANALYSIS_RESULT(state: PolicyState, { result, loading }: any) {
      state.policyAnalysis = { loading, result }
    },
  }),

  actions: defineActions({
    async OPEN_POLICY_PR(ctx, payload: { description: string }): Promise<PullRequest> {
      const { state, dispatch, commit } = policyActionContext(ctx)
      const {
        currentPolicy: { id },
        currentBranch,
      } = state

      commit.SET_POLICY_LOADING(true)
      await dispatch.SAVE_CURRENT_POLICY({
        operationType: 'SAVE_BEFORE_PR',
      })

      try {
        const { data: pullRequest } = await ApiService.openPolicyPullRequest(id, currentBranch, payload)
        commit.SET_POLICY_PULL_REQUEST(pullRequest)
        return pullRequest
      } finally {
        commit.SET_POLICY_LOADING(false)
        dispatch.POLL_POLICY_PR_STATUS({ policyId: id, branch: currentBranch })
      }
    },

    async DELETE_POLICY_PR(ctx): Promise<void> {
      const { state, commit } = policyActionContext(ctx)
      const {
        currentPolicy: { id },
        currentBranch,
      } = state

      commit.SET_POLICY_LOADING(true)

      await ApiService.deletePolicyPullRequest(id, currentBranch)

      commit.DELETED_POLICY_PULL_REQUEST()
      commit.SET_POLICY_LOADING(false)
    },

    async GET_POLICY_PR(
      ctx,
      { policyId, branch }: { policyId: string; branch: string }
    ): Promise<PullRequest | undefined> {
      const { commit, dispatch } = policyActionContext(ctx)
      try {
        commit.SET_IS_PR_LOADING(true)
        const { data: pullRequest } = await ApiService.getPolicyPullRequest(policyId, draftBranchName(branch))

        if (pullRequest.state === 'OPEN') {
          dispatch.POLL_POLICY_PR_STATUS({ policyId, branch })
        }
        commit.SET_POLICY_PULL_REQUEST(pullRequest)

        return pullRequest
      } finally {
        commit.SET_IS_PR_LOADING(false)
      }
    },

    async POLL_POLICY_PR_STATUS(ctx, { policyId, branch }: { policyId: string; branch: string }): Promise<void> {
      const { commit, dispatch, state, getters, rootDispatch } = policyActionContext(ctx)
      const { currentBranch } = state

      // fix - stop polling for PR if branch changed
      if (currentBranch != branch) {
        return
      }

      try {
        await dispatch.GET_CURRENT_POLICY_BRANCHES(policyId)
      } catch {
        setTimeout(() => dispatch.POLL_POLICY_PR_STATUS({ policyId, branch }), 10000)
        return
      }

      if (!getters.branchNames.includes(currentBranch)) {
        commit.SET_POLICY_PULL_REQUEST(undefined)
        getRouter()?.push(paths.policyBuilder.replace(':policy_id', policyId).replace(':branch?', GIT_RELEASE_BRANCH))

        commit.SET_IS_PR_MERGING(false)
        rootDispatch.SHOW_NOTIFICATION({
          message: notifications.successfullyMerged(policyId),
        })
        return
      }

      // Check if PR has been approved
      const { data: pullRequest } = await ApiService.getPolicyPullRequest(policyId, draftBranchName(branch))
      if (pullRequest.state === 'OPEN') commit.SET_POLICY_PULL_REQUEST(pullRequest)

      // uncomment it to enable poll mechanism (CBRMS-2147)
      //setTimeout(
      //  () => dispatch.POLL_POLICY_PR_STATUS({ policyId, branch }),
      //  10000
      //);
    },

    async CREATE_POLICY(
      ctx,
      policy: {
        id: string
        api: ApiSchema
        jira_issue: string
        rules?: Rule[]
        description: string
        labels?: string[]
        unittests?: PolicyTest[]
        content_helpers?: string[]
        content_inputs?: string[]
        format_version: number | undefined
        production_enabled?: boolean
        deployment_region: DeploymentRegion
      }
    ): Promise<Policy> {
      const {
        commit,
        rootState: { scriptKey },
        rootDispatch,
      } = policyActionContext(ctx)
      commit.SET_POLICY_LOADING(true)

      const newPolicy: CreatePolicyRequest = {
        id: policy.id,
        format_version: policy.format_version || LATEST_SUPPORTED_POLICY_FORMAT_VERSION,
        api: policy.api,
        jira_issue: policy.jira_issue,
        description: policy.description,
        labels: policy.labels || [],
        content_inputs: policy.content_inputs || [],
        content_helpers: policy.content_helpers || [],
        unittests: policy.unittests || [],
        production_enabled: policy.production_enabled,
        deployment_region: policy.deployment_region,
      }

      try {
        const { data: savedPolicy } = await ApiService.createPolicy(newPolicy)
        // Ensure a draft branch exists
        const { data: policyDraft } = await ApiService.updatePolicy(policy.id, {
          ...savedPolicy,
          rules: policy.rules ? policy.rules : [],
          unittests: policy.unittests ? policy.unittests : [],
          commit_message: 'create draft',
        })

        if (!scriptKey.inputs) {
          await rootDispatch.scriptKey.GET_INPUTS_METADATA()
        }

        if (!scriptKey.helpers) {
          await rootDispatch.scriptKey.GET_HELPERS_METADATA()
        }

        return policyDraft
      } finally {
        commit.SET_POLICY_LOADING(false)
      }
    },

    // eslint-disable-next-line no-unused-vars
    async GET_POLICY(ctx, { id, branch }: { id: string; branch?: string }): Promise<Policy> {
      const { data } = await ApiService.getPolicy(id, branch || GIT_RELEASE_BRANCH)
      return data
    },

    async GET_CURRENT_POLICY(ctx, { id, branch }: { id: string; branch?: string }): Promise<Policy> {
      const { commit, dispatch, rootDispatch, getters, rootState, rootGetters } = policyActionContext(ctx)

      commit.SET_POLICY_LOADING(true)
      // Reset anything related to previously loaded policy
      commit.SET_CURRENT_POLICY({ policy: {} as Policy })
      commit.SET_POLICY_TEST_RESULTS(undefined)
      commit.SET_POLICY_SHOWN_TEST_RESULT_INDEX(-1)
      commit.SET_POLICY_RUN_VALUES({ inputs: {}, helpers: {} })
      commit.SET_POLICY_RUN_RESULT(undefined)
      commit.SET_POLICY_PULL_REQUEST(undefined)
      commit.SET_POLICY_DEPLOYMENT_STATUS(undefined)
      commit.SET_POLICY_VERSION(undefined)
      commit.SET_POLICY_ENGINE_VERSION(undefined)
      commit.SET_DRAFT_IS_OUTDATED(false)

      const branchesPromise = dispatch.GET_CURRENT_POLICY_BRANCHES(id)

      if (!branch) {
        await branchesPromise
        branch = getters.branchNames.includes(rootState.auth.user!.username)
          ? rootState.auth.user!.username
          : GIT_RELEASE_BRANCH
      }

      commit.SET_CURRENT_POLICY_BRANCH(branch)

      try {
        let { data: policy } = await ApiService.getPolicy(id, branch)
        policy = cleanAwayInvalidKeysFromPolicyRules(policy, rootGetters.scriptKey.scriptKeysLookup, msg =>
          rootDispatch.SHOW_NOTIFICATION({ message: msg })
        )

        policy = migratePolicy(rootGetters.scriptKey.scriptKeysLookup, policy)

        if (policy.format_version > LATEST_SUPPORTED_POLICY_FORMAT_VERSION) {
          alertUser(
            'The loaded policy may not be compatible with your client. Please refresh and reload at your earliest convenience.'
          )
        }

        commit.SET_CURRENT_POLICY({ policy })
        commit.SET_PRISTINE_CURRENT_POLICY(deepClone(policy))
        return policy
      } catch (err) {
        if (axios.isAxiosError(err)) {
          if (err && err.response && err.response.status === 404) {
            // Mute error. We display a more subtle error to the user.
            // eslint-disable-next-line prettier/prettier
            ;(err as any).silent = true
          }
        }

        throw err
      } finally {
        commit.SET_POLICY_LOADING(false)
      }
    },

    async GET_CURRENT_POLICY_DEPLOYMENT_STATUS(ctx): Promise<PolicyDeploymentStatus> {
      const { state, commit, dispatch } = policyActionContext(ctx)
      commit.SET_POLICY_DEPLOYMENT_STATUS_LOADING_STATE('loading')
      const { data: deploymentStatus } = await ApiService.getDeploymentStatus(
        state.currentBranch,
        state.currentPolicy.id
      )
      commit.SET_POLICY_DEPLOYMENT_STATUS_LOADING_STATE('success')
      commit.SET_POLICY_DEPLOYMENT_STATUS(deploymentStatus)
      if (deploymentStatus.deployment_status === 'FINISHED') {
        dispatch.GET_POLICY_VERSION()
      }
      if (!isInTerminalState(deploymentStatus)) {
        dispatch.POLL_STACK_STATUS_CURRENT_POLICY()
      }
      return deploymentStatus
    },

    async GET_POLICY_VERSION(ctx) {
      const {
        state: { currentPolicy, policyDeploymentStatus },
        commit,
      } = policyActionContext(ctx)
      const policyName = currentPolicy.id
      const branch = currentPolicy.branch_name
      const url: string = policyDeploymentStatus ? policyDeploymentStatus.url : ''
      const regionName = getRegionNameFromUrl(url)
      if (!regionName) return

      const {
        data: { policy_version, engine_version },
      } = await ApiService.getPolicyVersion(policyName, branch || GIT_RELEASE_BRANCH, regionName!)
      commit.SET_POLICY_VERSION(policy_version)
      commit.SET_POLICY_ENGINE_VERSION(engine_version)
    },

    POLL_STACK_STATUS_CURRENT_POLICY(ctx): Promise<string> {
      const { commit, state, dispatch, rootDispatch } = policyActionContext(ctx)
      return new Promise(resolve => {
        ApiService.getDeploymentStatus(state.currentBranch, state.currentPolicy.id)
          .then(response => {
            const status = response.data
            commit.SET_POLICY_DEPLOYMENT_STATUS(status)
            commit.SET_DEPLOYING_FLAG(true)

            if (isInTerminalState(status)) {
              commit.SET_DEPLOYING_FLAG(false)
              rootDispatch.SHOW_NOTIFICATION({
                message: getDeploymentMessage(status),
              })
              resolve(status.status)
              return
            }

            setTimeout(() => dispatch.POLL_STACK_STATUS_CURRENT_POLICY(), 3000)
          })
          .catch(() => setTimeout(() => dispatch.POLL_STACK_STATUS_CURRENT_POLICY(), 3000))
      })
    },

    async UPDATE_POLICY_METADATA(ctx): Promise<void> {
      const {
        state,
        commit,
        getters,
        rootState: { auth },
      } = policyActionContext(ctx)
      const rules = state.currentPolicy.rules || []

      const outdatedRules = rules.reduce<{ id: string; isDraft: boolean }[]>((acc, rule) => {
        if (rule.branch_name === 'release') {
          if (rule.current_commit !== rule.latest_release_commit) {
            acc.push({ id: rule.id, isDraft: true })
            return acc
          } else {
            return acc
          }
        } else {
          if (rule.current_commit !== rule.latest_commit) {
            acc.push({ id: rule.id, isDraft: false })
            return acc
          } else {
            return acc
          }
        }
      }, [])

      const ruleIds = state.currentPolicy.rules.map(r => r.id)

      // Update rules in batches to prevent a large number of concurrent requests
      // to the corresponding `GET` endpoint reducing the risk of potential timeouts
      const updatedRules: Rule[] = []
      const outdatedRulesBatches = chunk(outdatedRules, OUTDATED_RULES_BATCH_SIZE)

      for (const batch of outdatedRulesBatches) {
        const res = await Promise.all(
          batch.map(r => {
            if (r.isDraft) {
              return ApiService.getRule(r.id, GIT_RELEASE_BRANCH)
            } else {
              return ApiService.getRule(r.id, auth.user!.username)
            }
          })
        )

        updatedRules.push(...res.map(r => r.data))
      }

      // We accumulate all the challenger rules with the respective info
      rules.forEach(rule => {
        if (Object.hasOwn(rule, 'champion_id')) {
          const index = updatedRules.findIndex(el => el.id === rule.id)
          const updatedRule = updatedRules[index]

          const updatedChampionRuleIndex = updatedRules.findIndex(el => el.id === rule.champion_id)
          const updatedChampionRule = updatedRules[updatedChampionRuleIndex]
          const outdatedChampionRule = rules.find(el => el.id === rule.champion_id)

          // Retaining the "selection_rate" from the old rules, both for the champion and the challenger
          updatedRules[updatedChampionRuleIndex] = { ...outdatedChampionRule, ...updatedChampionRule }
          updatedRules[index] = { ...rule, ...updatedRule }
        }
      })
      updatedRules.forEach(rule => {
        const index = ruleIds.indexOf(rule.id)
        commit.REPLACE_RULE_IN_CURRENT_POLICY({ rule, index })
      })

      const { inputs, helpers } = getters.currentPolicyUsedKeys

      await commit.SET_CONTENT_METADATA({
        content_inputs: inputs,
        content_helpers: helpers,
      })
    },

    async SAVE_CURRENT_POLICY(
      ctx,
      args?: {
        operationType: 'SAVE' | 'SAVE_BEFORE_PR' | 'CREATE_NEW_BRANCH'
      }
    ): Promise<Policy> {
      const { operationType = 'SAVE' } = args || {}
      const { state, commit, dispatch, rootState, rootGetters, rootDispatch, getters } = policyActionContext(ctx)
      const { currentPolicy } = state

      const hasOutdatedRules = getters.currentPolicyHasOutdatedRules

      if (['SAVE', 'SAVE_BEFORE_PR'].includes(operationType)) {
        const hasChange = getters.currentPolicyHasChanges

        if (!hasChange && !hasOutdatedRules && operationType === 'SAVE') {
          rootDispatch.SHOW_NOTIFICATION({
            message: notifications.skipSave,
            state: 'info',
          })
        }

        if (!hasChange && !hasOutdatedRules) {
          return state.currentPolicy
        }
      }
      commit.SET_POLICY_LOADING(true)

      try {
        if (hasOutdatedRules) {
          await dispatch.UPDATE_POLICY_METADATA()
        }

        const usedKeys = getters.currentPolicyUsedKeys

        // FIXME: Temporary fix to workaround AWS Lambda response size limit by removing
        // large `fraud_reject_blocked_card_identifier` rule from `fraud_us_plip` policy
        // This prevents `PUT 504` responses from the underlying backend service
        if (isCreateNewBranch(operationType) && currentPolicy.id === POLICY_FRAUD_US_PLIP_ID) {
          currentPolicy.rules = currentPolicy.rules.filter(
            rule => rule.id !== RULE_FRAUD_REJECT_BLOCKED_CARD_IDENTIFIER_ID
          )
        }

        const { data: updatedPolicy } = await ApiService.updatePolicy(currentPolicy.id, {
          ...currentPolicy,
          unittests: testValueUtils.sanitizeTests(
            rootGetters.scriptKey.scriptKeysLookup,
            currentPolicy.unittests || [],
            usedKeys
          ),
          content_inputs: usedKeys.inputs,
          content_helpers: usedKeys.helpers,
          commit_message: 'Save',
        })
        commit.SET_CURRENT_POLICY({
          policy: updatedPolicy,
        })
        commit.SET_CURRENT_POLICY_BRANCH(rootState.auth.user!.username)
        commit.SET_PRISTINE_CURRENT_POLICY(deepClone(updatedPolicy))

        rootDispatch.SHOW_NOTIFICATION({
          message: {
            SAVE: notifications.successfullySave,
            SAVE_BEFORE_PR: notifications.successfullySave,
            CREATE_NEW_BRANCH: notifications.successfullyCreateDraft,
          }[operationType],
        })

        return updatedPolicy
      } finally {
        commit.SET_POLICY_LOADING(false)
      }
    },

    async DEPLOY_CURRENT_POLICY(ctx, payload): Promise<void> {
      const { commit, state, dispatch } = policyActionContext(ctx)
      const { currentPolicy } = state

      if (state.currentBranch !== GIT_RELEASE_BRANCH) {
        const { data: releaseDeploymentStatus } = await ApiService.getDeploymentStatus(
          GIT_RELEASE_BRANCH,
          state.currentPolicy.id
        )

        if (!['CREATE_COMPLETE', 'UPDATE_COMPLETE'].includes(releaseDeploymentStatus.status)) {
          window.alert('In order to deploy shadow of your draft, please first deploy the release version of the policy')
          return
        }
      }

      commit.SET_DEPLOYING_FLAG(true)
      try {
        await ApiService.deployPolicy(
          currentPolicy.id,
          state.currentBranch,
          state.engineVersion || undefined,
          payload.draftFunctions?.length
            ? payload.draftFunctions.map((f: { [x: string]: string }) => f['id']).join()
            : undefined
        )

        // TODO: this looks like a dirty hack fix for the fact that deployment status only updates
        // asynchronously after a successful deployment request
        setTimeout(() => {
          dispatch.POLL_STACK_STATUS_CURRENT_POLICY()
        }, 2500)
      } finally {
        commit.SET_DEPLOYING_FLAG(false)
      }
    },

    async GET_POLICY_WARNINGS(ctx): Promise<PolicyWarnings> {
      const { state } = policyActionContext(ctx)
      const { currentPolicy } = state

      const { data: warnings } = await ApiService.getPolicyWarnings(currentPolicy.id)

      return warnings
    },

    async GET_POLICY_ANALYSIS_RESULT(ctx, correlationId?: string): Promise<void> {
      const { commit, state } = policyActionContext(ctx)
      const {
        currentPolicy: { id },
      } = state
      commit.SET_POLICY_ANALYSIS_RESULT({
        loading: true,
      })
      return ApiService.getPolicyAnalysis(id, correlationId)
        .then(({ data }) => {
          commit.SET_POLICY_ANALYSIS_RESULT({
            loading: false,
            result: data,
          })
        })
        .catch(error => {
          commit.SET_POLICY_ANALYSIS_RESULT({
            loading: false,
            result: error.response.data.message,
          })
        })
    },

    async MERGE_PULL_REQUEST(ctx): Promise<void> {
      const { commit, state, dispatch } = policyActionContext(ctx)
      const { currentPolicy } = state

      if (!state.currentPr) return

      commit.SET_IS_PR_MERGING(true)
      try {
        await ApiService.mergePolicyPullRequest(currentPolicy.id, state.currentBranch, {
          id: state.currentPr.id,
          version: state.currentPr.version,
        })

        dispatch.POLL_POLICY_PR_STATUS({
          policyId: currentPolicy.id,
          branch: state.currentBranch,
        })
      } catch (e) {
        commit.SET_IS_PR_MERGING(false)
        throw new Error(
          `We couldn't merge your Pull Request.
          Please <strong>make sure all required checks are passing</strong>, then try again.<br><br>
          If the issue persists, please <strong>contact us</strong>.`
        )
      }
    },

    async DELETE_CURRENT_POLICY_DEPLOYMENT(ctx): Promise<void> {
      const { commit, state, dispatch } = policyActionContext(ctx)

      const { currentPolicy, currentBranch } = state

      // Just in case
      if (currentBranch === GIT_RELEASE_BRANCH && isProduction) {
        throw new Error('Sorry, no deleting production deployments')
      }

      commit.SET_DEPLOYING_FLAG(true)

      try {
        await ApiService.deleteDeployment(currentPolicy.id, state.currentBranch)
        dispatch.POLL_STACK_STATUS_CURRENT_POLICY()
      } finally {
        commit.SET_DEPLOYING_FLAG(false)
      }
    },

    REMOVE_RULE_FROM_CURRENT_POLICY(ctx, ruleIds: string[]) {
      const { commit, getters } = policyActionContext(ctx)
      commit.REMOVE_RULE_FROM_CURRENT_POLICY(ruleIds)

      const { inputs, helpers } = getters.currentPolicyUsedKeys

      commit.SET_CONTENT_METADATA({
        content_inputs: inputs,
        content_helpers: helpers,
      })
    },

    async ADD_RULE_TO_CURRENT_POLICY(ctx, ruleIds: string[]): Promise<void> {
      const { commit, getters } = policyActionContext(ctx)
      commit.SET_POLICY_LOADING(true)

      try {
        const rules = await Promise.all(ruleIds.map(id => ApiService.getRule(id, GIT_RELEASE_BRANCH)))

        const rulesWithCurrentCommit = rules.map(({ data: r }) => ({
          ...r,
          current_commit: r.latest_commit,
        }))

        commit.ADD_RULE_TO_CURRENT_POLICY({
          rulesToAdd: rulesWithCurrentCommit,
        })
        const { helpers, inputs } = getters.currentPolicyUsedKeys
        commit.SET_CONTENT_METADATA({
          content_helpers: helpers,
          content_inputs: inputs,
        })
      } finally {
        commit.SET_POLICY_LOADING(false)
      }
    },

    UPDATE_CHAMPION_IN_AB_TEST(ctx, { championId, championRate }: { championId: string; championRate: number }) {
      const { state, commit } = policyActionContext(ctx)
      const champion: Rule = state.currentPolicy.rules.find(el => el.id === championId)!
      const championIndex = state.currentPolicy.rules.indexOf(champion!)
      const updatedChampion: Rule = {
        ...champion,
        selection_rate: championRate,
      }
      commit.REPLACE_RULE_IN_CURRENT_POLICY({
        rule: updatedChampion,
        index: championIndex,
      })
    },

    async ADD_AB_TEST_TO_CURRENT_POLICY(
      ctx,
      {
        ruleId,
        championId,
        championRate,
        selectionRate,
      }: {
        ruleId: string
        championId: string
        championRate: number
        selectionRate: number
      }
    ): Promise<void> {
      const { commit, dispatch, getters } = policyActionContext(ctx)
      commit.SET_POLICY_LOADING(true)
      try {
        const { data: rule } = await ApiService.getRule(ruleId, GIT_RELEASE_BRANCH)

        const ruleWithAbAttributes = {
          ...rule,
          champion_id: championId,
          selection_rate: selectionRate,
        }
        commit.ADD_RULE_TO_CURRENT_POLICY({
          rulesToAdd: [ruleWithAbAttributes],
          isChallenger: true,
        })

        dispatch.UPDATE_CHAMPION_IN_AB_TEST({ championId, championRate })
        const { helpers, inputs } = getters.currentPolicyUsedKeys
        commit.SET_CONTENT_METADATA({
          content_helpers: helpers,
          content_inputs: inputs,
        })
      } finally {
        commit.SET_POLICY_LOADING(false)
      }
    },

    async SET_AB_WINNER(
      ctx,
      { winnerId, index, rulesToRemove }: { winnerId: string; index: number; rulesToRemove: string[] }
    ) {
      const { commit, dispatch } = policyActionContext(ctx)
      commit.SET_POLICY_LOADING(true)

      await dispatch.REMOVE_RULE_FROM_CURRENT_POLICY(rulesToRemove)

      dispatch.REPLACE_RULE_IN_CURRENT_POLICY({
        ruleId: winnerId,
        branch: GIT_RELEASE_BRANCH,
        index,
      })
      commit.SET_POLICY_LOADING(false)
    },

    async RUN_POLICY(ctx): Promise<void> {
      const { commit, state, getters, rootGetters } = policyActionContext(ctx)

      const includedRules = getters.displayedRules.map((rule: Rule) => {
        const isChallenger = Object.hasOwn(rule, 'champion_id')
        if (isChallenger) {
          return {
            id: rule.id,
            branch_name: rule.branch_name,
            content: rule.content,
            champion_id: rule.champion_id,
            selection_rate: rule.selection_rate,
            ...(Object.hasOwn(rule, 'champion_rate') && {
              champion_rate: rule.champion_rate,
            }),
          }
        } else {
          return {
            id: rule.id,
            branch_name: rule.branch_name,
            content: rule.content,
          }
        }
      })

      const { usedValues } = state
      const usedKeys = getters.currentPolicyUsedKeys

      const { inputs, helpers } = testValueUtils.sanitizeTest(
        rootGetters.scriptKey.scriptKeysLookup,
        usedValues,
        usedKeys
      )

      const payload = {
        inputs,
        helpers,
        policy: {
          id: state.currentPolicy.id,
          rules: includedRules,
          branch_name: state.currentBranch,
        },
      }
      const { data: result } = await ApiService.getPolicyExecutionResults(payload as PolicyExecPayload)
      commit.SET_POLICY_RUN_RESULT(result)
    },

    async LOAD_INITIAL_RUN_VALUES(ctx, request: RequestTemplate): Promise<void> {
      const {
        commit,
        getters,
        rootState: { scriptKey: scriptKeyState },
      } = policyActionContext(ctx)

      const initialHelpersValues = scriptKeyState.helpers!
      const usedKeys = getters.currentPolicyUsedKeys

      const commonInputs: string[] = intersection(keys(request.request_dict), usedKeys.inputs)
      const commonHelpers: string[] = intersection(keys(initialHelpersValues), usedKeys.helpers)

      const payload: UsedValues = {
        inputs: keys(request.request_dict)
          .filter(key => indexOf(commonInputs, key) !== -1)
          .reduce(
            (acc, key) => ({
              ...acc,
              [key]: stringifyIfObject(request.request_dict[key]),
            }),
            {}
          ),
        helpers: keys(initialHelpersValues)
          .filter(key => indexOf(commonHelpers, key) !== -1)
          .reduce(
            (acc, key) => ({
              ...acc,
              [key]: stringifyIfObject(initialHelpersValues[key].sample_value),
            }),
            {}
          ),
      }

      commit.SET_POLICY_RUN_VALUES(payload)
    },

    async GET_CURRENT_POLICY_BRANCHES(ctx, id: string): Promise<PolicyBranchInfo[]> {
      const { commit } = policyActionContext(ctx)
      const { data: result } = await ApiService.getPolicyBranches(id)

      const releaseInfo = result.find(({ branch }) => branch === GIT_RELEASE_BRANCH)!
      const resultWithoutRelease = result.filter(({ branch }) => branch !== GIT_RELEASE_BRANCH)

      resultWithoutRelease.sort((a, b) => {
        if (a.branch < b.branch) {
          return -1
        }
        if (a.branch > b.branch) {
          return 1
        }

        return 0
      })

      commit.SET_CURRENT_POLICY_BRANCHES([releaseInfo, ...resultWithoutRelease])
      return result
    },

    ADD_POLICY_TEST(ctx, test: PolicyTest): void {
      const { commit, state } = policyActionContext(ctx)
      const unittests = state.currentPolicy.unittests || []
      commit.SET_POLICY_TESTS([...unittests, test])
    },

    REMOVE_POLICY_TEST(ctx, idxToRemove: number): void {
      const { commit, state } = policyActionContext(ctx)

      const unittests = state.currentPolicy.unittests || []
      commit.SET_POLICY_TESTS(unittests.filter((_: PolicyTest, idx: number) => idx !== idxToRemove))
    },

    MOVE_POLICY_TEST(ctx, { fromIndex, toIndex }: { fromIndex: number; toIndex: number }): void {
      const { commit, state } = policyActionContext(ctx)
      const unittests = [...state.currentPolicy.unittests!]

      unittests.splice(toIndex, 0, unittests.splice(fromIndex, 1)[0])
      commit.SET_POLICY_TESTS(unittests)

      // If there are test results, move them along with the policy test.
      const testResults = [...(state.testResults || [])]
      testResults.splice(toIndex, 0, testResults.splice(fromIndex, 1)[0])
      commit.SET_POLICY_TEST_RESULTS(testResults)
    },

    async RUN_POLICY_TESTS(ctx): Promise<PolicyTestResult[] | undefined> {
      const { commit, state, getters, rootDispatch, rootGetters } = policyActionContext(ctx)

      const sanitizedUnitTests: PolicyTest[] = testValueUtils.sanitizeTests(
        rootGetters.scriptKey.scriptKeysLookup,
        state.currentPolicy.unittests || [],
        getters.currentPolicyUsedKeys
      )

      const passesValidation = sanitizedUnitTests.reduce((acc: boolean, test: PolicyTest) => {
        return acc && requiredPolicyTestFields.map(field => !!test.inputs[field]).every(e => e === true)
      }, true)

      if (!passesValidation) {
        rootDispatch.SHOW_NOTIFICATION({
          message: `Please provide ${requiredPolicyTestFields.join(' and ')} in all test cases`,
          state: 'negative',
        })
        return Promise.resolve(undefined)
      }

      const includedRules = state.currentPolicy.rules.map((rule: Rule) => {
        const isChallenger = Object.hasOwn(rule, 'champion_id')
        if (isChallenger) {
          return {
            id: rule.id,
            branch_name: rule.branch_name,
            content: rule.content,
            champion_id: rule.champion_id,
            selection_rate: rule.selection_rate,
            ...(Object.hasOwn(rule, 'champion_rate') && {
              champion_rate: rule.champion_rate,
            }),
          }
        } else {
          return {
            id: rule.id,
            branch_name: rule.branch_name,
            content: rule.content,
          }
        }
      })

      const { data: testResults } = await ApiService.getPolicyTestResults(false, {
        unittests: sanitizedUnitTests,
        policy: {
          id: state.currentPolicy.id,
          rules: includedRules,
          extensions: state.currentPolicy.extensions || undefined,
        },
      })

      commit.SET_POLICY_TEST_RESULTS(testResults)

      return testResults
    },

    async RUN_ALL_POLICY_AND_RULES_TESTS(ctx): Promise<PolicyTestResult[] | undefined> {
      const { commit, state, getters, rootDispatch, rootGetters } = policyActionContext(ctx)

      const sanitizedUnitTests: PolicyTest[] = testValueUtils.sanitizeTests(
        rootGetters.scriptKey.scriptKeysLookup,
        state.currentPolicy.unittests || [],
        getters.currentPolicyUsedKeys
      )

      const passesValidation = sanitizedUnitTests.reduce((acc: boolean, test: PolicyTest) => {
        return acc && requiredPolicyTestFields.map(field => !!test.inputs[field]).every(e => e === true)
      }, true)

      if (!passesValidation) {
        rootDispatch.SHOW_NOTIFICATION({
          message: `Please provide ${requiredPolicyTestFields.join(' and ')} in all test cases`,
          state: 'negative',
        })
        return Promise.resolve(undefined)
      }

      const includedRules = state.currentPolicy.rules.map((rule: Rule) => {
        const isChallenger = Object.hasOwn(rule, 'champion_id')
        if (isChallenger) {
          return {
            id: rule.id,
            branch_name: rule.branch_name,
            content: rule.content,
            champion_id: rule.champion_id,
            selection_rate: rule.selection_rate,
            unittests: rule.unittests,
            ...(Object.hasOwn(rule, 'champion_rate') && {
              champion_rate: rule.champion_rate,
            }),
          }
        } else {
          return {
            id: rule.id,
            branch_name: rule.branch_name,
            content: rule.content,
            unittests: rule.unittests,
          }
        }
      })

      const { data: testResults } = await ApiService.getPolicyTestResults(true, {
        unittests: sanitizedUnitTests,
        policy: {
          id: state.currentPolicy.id,
          rules: includedRules,
          extensions: state.currentPolicy.extensions || undefined,
        },
      })

      commit.SET_POLICY_TEST_RESULTS(testResults)

      return testResults
    },

    async DELETE_CURRENT_DRAFT(ctx, { policyId, branch }: { policyId: string; branch: string }) {
      const { commit, rootDispatch } = policyActionContext(ctx)
      commit.SET_POLICY_LOADING(true)
      try {
        await ApiService.deletePolicyBranch(policyId, branch)
        rootDispatch.SHOW_NOTIFICATION({
          message: notifications.successfullyDeleteDraft,
          state: 'info',
        })
        getRouter()?.push(paths.policyBuilder.replace(':policy_id', policyId).replace(':branch?', GIT_RELEASE_BRANCH))
      } finally {
        commit.SET_POLICY_LOADING(false)
      }
    },

    async DELETE_POLICY(ctx, policyId: string) {
      const { commit, rootDispatch } = policyActionContext(ctx)
      commit.SET_POLICY_LOADING(true)
      try {
        await ApiService.deletePolicy(policyId)
        rootDispatch.SHOW_NOTIFICATION({
          message: notifications.successfullyDeletePolicy,
          state: 'info',
        })
        getRouter()?.push(paths.home)
      } finally {
        commit.SET_POLICY_LOADING(false)
      }
    },

    async REPLACE_RULE_IN_CURRENT_POLICY(
      ctx,
      { ruleId, branch, index }: { ruleId: string; branch?: string; index: number }
    ) {
      const { commit, getters } = policyActionContext(ctx)
      commit.SET_POLICY_LOADING(true)
      try {
        const { data: rule } = await ApiService.getRule(ruleId, branch || GIT_RELEASE_BRANCH)
        commit.REPLACE_RULE_IN_CURRENT_POLICY({ rule, index })
        const { helpers, inputs } = getters.currentPolicyUsedKeys
        commit.SET_CONTENT_METADATA({
          content_helpers: helpers,
          content_inputs: inputs,
        })
      } finally {
        commit.SET_POLICY_LOADING(false)
      }
    },

    async POLL_IS_CURRENT_DRAFT_OUTDATED(ctx, { policyId, branch }: { policyId: string; branch: string }) {
      const { dispatch, commit } = policyActionContext(ctx)

      const res = await dispatch.GET_POLICY({ id: policyId, branch })
      const sourceCommit = res.source_commit
      const latestReleaseCommit = res.latest_release_commit

      if (sourceCommit && sourceCommit !== latestReleaseCommit) {
        commit.SET_DRAFT_IS_OUTDATED(true)
        return
      }

      dispatch.CLEAR_TIMEOUT()

      pollingTimer = window.setTimeout(() => dispatch.POLL_IS_CURRENT_DRAFT_OUTDATED({ policyId, branch }), 60000)
    },

    CLEAR_TIMEOUT() {
      clearTimeout(pollingTimer)
    },
  }),
})

export default policyModule
